import {Component, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import * as Showdown from 'showdown';

import {Apptext} from 'shared/classes/apptext';
import {SearchBarComponent} from 'shared/search-bar/search-bar.component';
import {ApptextsService} from './services/apptexts.service';
import { TargetGroup, TARGETGROUP_DUMMIES } from 'shared/models/target-groups/target-group';
import { Company } from 'shared/classes/company';
import { ModalAddTextComponent } from './modal-add-text/modal-add-text.component';
import { ModalConfirmationComponent } from 'shared/modals/modal-confirmation/modal-confirmation.component';

const MARK_DOWN_TEXTPARTS = ['.MAIN', '.INFO', '.MATERIAL', '.CONTACT'];

@Component({
  selector: 'apptexts',
  templateUrl: './apptexts.component.html',
  styleUrls: ['./apptexts.component.css'],
})
export class ApptextsComponent implements OnInit {

  @ViewChild('companyModal') private companyModal: ModalAddTextComponent;
  @ViewChild('groupModal') private groupModal: ModalAddTextComponent;

  @ViewChild('deleteModal') private deleteModal: ModalConfirmationComponent;

  public texts: Array<Apptext> = [];
  public filteredTexts: Array<Apptext> = [];
  public fieldsToSearch: Array<Array<string>> = [];
  public filter: string;
  public currentPage: Event;
  public groups: Array<string> = [];
  public companies: Array<string> = [];
  public doShowMarkDownOutput: Dictionary<Dictionary<boolean>> = {};

  private converter: Showdown.Converter = new Showdown.Converter();

  constructor(private router: Router,
              private activatedRoute: ActivatedRoute,
              private apptextsService: ApptextsService) { 
    this.converter.setFlavor('github')
  }

  public ngOnInit(): void {
    this.apptextsService.getTexts()
      .subscribe((texts: Array<Apptext>) => {
        this.texts = texts.sort((a,b) => a.name.localeCompare(b.name)).map(text => {
          if (this.canMarkDown(text.name) && text.text !== undefined) {
            if (text.text.de !== undefined) {
              text.text.de = text.text.de.replace(/\\n/g, "\n");
            }
            if (text.text.en !== undefined) {
              text.text.en = text.text.en.replace(/\\n/g, "\n");
            }
          }
          return text;
        });
        this.filteredTexts = texts;
        this._setFieldsToSearch();
      });
      this.apptextsService.getAllTargetGroups()
      .subscribe((groups: Array<TargetGroup>) => {
        this.groups = groups
          .map(group => group.title)
          .filter(name => !TARGETGROUP_DUMMIES.includes(name));
      });
      this.apptextsService.getAllCompanies()
      .subscribe((companies: Array<Company>) => {
        this.companies = companies.map(company => company.name);
      });
  }

  public markSpecial(name: string): string {
    let res: string = name;
    if ( res.indexOf('COREFACTS.COMPANY.') == 0 ) {
      const i1 = res.indexOf('.', 18);
      if ( i1 != -1 ) {
        const compCand = res.substring(18, i1);
        if ( this.companies.find(company => company.toUpperCase() == compCand) !== undefined ) {
          res = res.substring(0, 18)+'<span class="company">'+res.substring(18, i1)+'</span>'+res.substring(i1);
        }
      } 
    }
    const i = res.indexOf('@');
    if ( i != -1 ) {
      res = res.substring(0, i)+'<span class="special">'+res.substring(i)+'</span>';
    }
    return res;
  }

  public canMarkDown(name: string): boolean {
    return this.canCompany(name) && MARK_DOWN_TEXTPARTS.find(part => name.indexOf(part) !== -1) !== undefined;
  }

  public canCompany(name: string): boolean {
    return name.indexOf('COREFACTS.COMPANY.') == 0;
  }

  public isSpecial(name: string): boolean {
    if ( name.indexOf('@') != -1 ) {
      return true;
    }
    if ( !this.canCompany(name) ) {
      return false;
    }
    const i1 = name.indexOf('.', 18);
    if ( i1 != -1 ) {
      const compCand = name.substring(18, i1);
      if ( this.companies.find(company => company.toUpperCase() == compCand) !== undefined ) {
        return true;
      }
    }
    return false;
  }
  
  public setText(apptext: Apptext): void {
    this.apptextsService.setText(apptext)
      .subscribe((updatedText: Apptext) => {
        this._setUpdatedText(updatedText);
      });
  }

  public addCompanyText(name: string) {
    let text = name;
    const i1 = name.indexOf('.', 18);
    if ( i1 != -1 ) {
      const compCand = name.substring(18, i1);
      if ( this.companies.find(company => company.toUpperCase() == compCand) !== undefined ) {
        text = name.substring(0, 18)+'???'+name.substring(i1);
      }
    }
    else {
      text = name.substring(0, 18)+'???.'+name.substring(18);
    }
    this.companyModal
      .setGroups(this.companies)
      .setText(text)
      .show();
  }

  public addGroupText(name: string) {
    let text = name;
    const i = name.indexOf('@');
    if ( i != -1 ) {
      text = name.substring(0, i);
    }
    this.groupModal
      .setGroups(this.groups)
      .setText(text+'@???')
      .show();
  }
  
  public addText(event, type) {
    // console.log("Add Text " + event.text + " " + event.group + ' ' + type);
    this.apptextsService.addText({name: event.text.replace('???', event.group.toUpperCase()), text:{ de: '', en: ''}})
      .subscribe((insertedText: Apptext) => {
        this._setInsertedText(insertedText);
      });
  }

  public showDeleteModal(text: Apptext): void {
    const content: string = `Sind Sie sicher, dass Sie den Content "${text.name}" löschen möchten?`;

    this.deleteModal.setContent(content)
      .setEventData(text)
      .show();
  }

  public deleteText(text: Apptext) {
    this.apptextsService.deleteText(text)
      .subscribe(() => {
      const index: number = this.texts.findIndex((text1: Apptext) => {
        return text1._id === text._id;
      });

      if (index > -1) {
        this.texts.splice(index, 1);
        this._setFieldsToSearch();
        // the form won't go back to pristine state without this
        this.texts = this.texts.splice(0);
      }
    });
  }

  public filterTexts(evenTexts: Array<Apptext>): void {
    this.filteredTexts = evenTexts;
  }

  public setMarkdownOutput(name: string, language: string, value: boolean): void {
    if ( this.doShowMarkDownOutput[name] === undefined ) {
      this.doShowMarkDownOutput[name] = {};
    }
    this.doShowMarkDownOutput[name][language] = value;
  }

  public updateMarkdown(name: string, language: string): void {
    //this.markdownOutput[this.language] = this.converter.makeHtml(this.content.description[this.language]);
  }

  public markdownOutput(s: string): string {
    if ( s !== undefined) {
      return this.converter.makeHtml(s);
    }
    return '';
  }

  public showMarkdownOutput(name: string, language: string): boolean {
    return this.doShowMarkDownOutput[name] !== undefined && this.doShowMarkDownOutput[name][language] !== undefined ? 
      this.doShowMarkDownOutput[name][language] : true;
  }

  private _setFieldsToSearch(): void {
    for (let textIndex: number = 0; textIndex < this.texts.length; textIndex++) {
      this._updateFieldsToSearch(textIndex);
    }
  }

  private _updateFieldsToSearch(textIndex: number): void {
    const text: Apptext = this.texts[textIndex];

    this.fieldsToSearch[textIndex] = [
      text.text.de.toLowerCase(),
      text.text.en.toLowerCase(),
      text.name.toLowerCase(),
    ];
  }

  private _setUpdatedText(updatedText: Apptext): void {
    const apptextIndex: number = this.texts.findIndex((apptext: Apptext) => {
      return apptext._id === updatedText._id;
    });

    if (apptextIndex < 0) {
      console.warn('apptext could not be updated, as it wasnt found', updatedText);
      return;
    }

    this.texts[apptextIndex] = updatedText;
    this._updateFieldsToSearch(apptextIndex);

    // the form won't go back to pristine state without this
    this.texts = this.texts.splice(0);
  }

  private _setInsertedText(insertedText: Apptext): void {
    this.texts.push(insertedText);
    this.texts = this.texts.sort((a,b) => a.name.localeCompare(b.name));

    this._setFieldsToSearch();
    // the form won't go back to pristine state without this
    this.texts = this.texts.splice(0);
  }
}



// WEBPACK FOOTER //
// /usr/src/app/src/app/modules/apptexts/apptexts.component.ts